import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import ReplaysList from './components/ReplaysList';
import ParsedReplayView from './components/ParsedReplayView';
import Sc2Calculator from './components/sc2/Sc2Calculator';
import ReplayPlayerSchemaView from './components/ReplayPlayerSchemaView';
import {
  getApiBaseUrl,
  storeBasicAuth,
  clearBasicAuth,
  hasBasicAuth,
  createApiAxios,
  authHeaders,
  probeAuth,
  handleUnauthorized,
} from './authUtils';

const API_BASE_URL = getApiBaseUrl();

const MAIN_VIEWS = new Set(['replays', 'calculator', 'schema']);

function initialView() {
  const view = new URLSearchParams(window.location.search).get('view');
  return MAIN_VIEWS.has(view) ? view : 'replays';
}

function App() {
  const [replays, setReplays] = useState([]);
  const [assetAvailability, setAssetAvailability] = useState({
    sc2: new Set(),
    parsed: new Set(),
    ai: new Set(),
  });
  const [selectedReplay, setSelectedReplay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState(initialView);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(() => hasBasicAuth());
  const [loginOpen, setLoginOpen] = useState(false);
  const [authError, setAuthError] = useState(null);

  const becomeGuest = useCallback(() => {
    handleUnauthorized();
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
    setLoginOpen(false);
  }, []);

  const fetchReplays = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const axiosInstance = await createApiAxios();
      const listResp = await axiosInstance.get('/replays/list');
      const replays = listResp.data.replays || [];
      setReplays(replays);
      const sc2 = new Set();
      const parsed = new Set();
      const ai = new Set();
      for (const replay of replays) {
        if (replay.has_sc2) sc2.add(replay.replay_id);
        if (replay.has_parsed) parsed.add(replay.replay_id);
        if (replay.has_ai) ai.add(replay.replay_id);
      }
      setAssetAvailability({ sc2, parsed, ai });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch replays');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReplays();
  }, [fetchReplays]);

  useEffect(() => {
    let cancelled = false;
    const restoreSession = async () => {
      if (!hasBasicAuth()) {
        if (!cancelled) {
          setIsAuthenticated(false);
          setAuthChecking(false);
        }
        return;
      }
      const ok = await probeAuth();
      if (!cancelled) {
        setIsAuthenticated(ok);
        setAuthChecking(false);
      }
    };
    restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.selectedReplayId) {
        const replay = replays.find(r => r.replay_id === event.state.selectedReplayId);
        if (replay) {
          setSelectedReplay(replay);
        }
      } else {
        setSelectedReplay(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [replays]);

  useEffect(() => {
    if (replays.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const replayId = urlParams.get('replay');
      if (replayId) {
        const replay = replays.find(r => String(r.replay_id) === String(replayId));
        if (replay) {
          setSelectedReplay(replay);
        }
      }
    }
  }, [replays]);

  const handleReplaySelect = (replay) => {
    setSelectedReplay(replay);
    const newState = { selectedReplayId: replay.replay_id };
    const newUrl = `${window.location.pathname}?replay=${replay.replay_id}`;
    window.history.pushState(newState, '', newUrl);
  };

  const handleBackToList = () => {
    setSelectedReplay(null);
    window.history.pushState(null, '', window.location.pathname);
  };

  const handleViewChange = (nextView) => {
    const safeView = MAIN_VIEWS.has(nextView) ? nextView : 'replays';
    setView(safeView);
    const url = safeView === 'replays'
      ? window.location.pathname
      : `${window.location.pathname}?view=${safeView}`;
    window.history.pushState(null, '', url);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError(null);
    storeBasicAuth(username, password);
    const ok = await probeAuth();
    if (ok) {
      setIsAuthenticated(true);
      setLoginOpen(false);
      setAuthError(null);
      return;
    }
    clearBasicAuth();
    setIsAuthenticated(false);
    setAuthError('Invalid credentials');
  };

  const handleLogout = () => {
    clearBasicAuth();
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
    setLoginOpen(false);
    setAuthError(null);
  };

  const handleTriggerUpdate = async () => {
    if (!isAuthenticated) {
      return;
    }
    try {
      setError(null);
      const response = await fetch(`${API_BASE_URL}/replays/update`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (response.status === 401) {
        becomeGuest();
        setError('Authentication failed — returned to Guest');
        return;
      }
      if (!response.ok) {
        throw new Error('Failed to trigger replay update');
      }
      await fetchReplays();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReplayDeleted = async () => {
    setSelectedReplay(null);
    window.history.pushState(null, '', window.location.pathname);
    await fetchReplays();
  };

  const handleReplayUpload = async (file) => {
    if (!isAuthenticated || !file) {
      return;
    }
    try {
      setError(null);
      const body = new FormData();
      body.append('file', file);
      const response = await fetch(`${API_BASE_URL}/replays/upload`, {
        method: 'POST',
        headers: authHeaders(),
        body,
      });
      if (response.status === 401) {
        becomeGuest();
        setError('Authentication failed — returned to Guest');
        return;
      }
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Upload failed');
      }
      const result = await response.json();
      const newUrl = `${window.location.pathname}?replay=${encodeURIComponent(result.replay_id)}`;
      window.history.pushState({ selectedReplayId: result.replay_id }, '', newUrl);
      await fetchReplays();
    } catch (err) {
      setError(err.message);
    }
  };

  if (view === 'replays' && loading) {
    return (
      <div className="App">
        <div className="loading">Loading replays...</div>
      </div>
    );
  }

  if (view === 'replays' && error) {
    return (
      <div className="App">
        <div className="error">
          Error: {error}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={fetchReplays} className="retry-btn">
              Retry
            </button>
            <button onClick={() => handleViewChange('calculator')} className="retry-btn">
              Открыть калькулятор
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>StarCraft II Replays Viewer</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <nav className="view-nav">
            <button
              onClick={() => handleViewChange('replays')}
              className={`view-nav-btn ${view === 'replays' ? 'active' : ''}`}
            >
              Реплеи
            </button>
            <button
              onClick={() => handleViewChange('calculator')}
              className={`view-nav-btn ${view === 'calculator' ? 'active' : ''}`}
            >
              Калькулятор
            </button>
            <button
              onClick={() => handleViewChange('schema')}
              className={`view-nav-btn ${view === 'schema' ? 'active' : ''}`}
            >
              Схема реплея
            </button>
          </nav>
          {view === 'replays' && selectedReplay && (
            <button onClick={handleBackToList} className="back-btn">
              ← Back to List
            </button>
          )}
        </div>
        <div className="basic-auth-form" style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '0.85rem' }}>
            {authChecking ? '…' : (isAuthenticated ? 'Authenticated User' : 'Guest')}
          </span>
          {!authChecking && isAuthenticated && (
            <button type="button" className="retry-btn" onClick={handleLogout}>
              Logout
            </button>
          )}
          {!authChecking && !isAuthenticated && !loginOpen && (
            <button type="button" className="retry-btn" onClick={() => setLoginOpen(true)}>
              Login
            </button>
          )}
          {!authChecking && !isAuthenticated && loginOpen && (
            <form onSubmit={handleLogin} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
              />
              <input
                type="password"
                placeholder="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <button type="submit" className="retry-btn">Login</button>
              <button
                type="button"
                className="retry-btn"
                onClick={() => {
                  setLoginOpen(false);
                  setAuthError(null);
                }}
              >
                Cancel
              </button>
              {authError && (
                <span style={{ fontSize: '0.85rem', color: '#c62828' }}>{authError}</span>
              )}
            </form>
          )}
        </div>
      </header>

      <main className="App-main">
        {view === 'calculator' ? (
          <Sc2Calculator />
        ) : view === 'schema' ? (
          <ReplayPlayerSchemaView />
        ) : selectedReplay ? (
          <ParsedReplayView
            replay={selectedReplay}
            assetAvailability={assetAvailability}
            apiBaseUrl={API_BASE_URL}
            isAuthenticated={isAuthenticated}
            onUnauthorized={becomeGuest}
            onDeleted={handleReplayDeleted}
          />
        ) : (
          <ReplaysList
            replays={replays}
            assetAvailability={assetAvailability}
            onReplaySelect={handleReplaySelect}
            onRefresh={fetchReplays}
            onTriggerUpdate={isAuthenticated ? handleTriggerUpdate : undefined}
            onUpload={isAuthenticated ? handleReplayUpload : undefined}
          />
        )}
      </main>
    </div>
  );
}

export default App;
